import React, { forwardRef, useId } from 'react';
import { cn } from '../../utils/cn';

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  containerClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  function Textarea(
    { id, label, hint, error, containerClassName, className, ...rest },
    ref
  ) {
    const reactId = useId();
    const fieldId = id || `textarea-${reactId}`;
    const hintId = hint ? `${fieldId}-hint` : undefined;
    const errorId = error ? `${fieldId}-error` : undefined;

    return (
      <div className={cn('space-y-1.5', containerClassName)}>
        {label && (
          <label
            htmlFor={fieldId}
            className="block text-body-sm font-medium text-neutral-700 dark:text-neutral-300"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={fieldId}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
          className={cn(
            'block w-full rounded-lg border bg-white dark:bg-neutral-900 px-3 py-2 text-body text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 placeholder:dark:text-neutral-500',
            'dark:bg-neutral-900 dark:text-neutral-100 dark:placeholder:text-neutral-500',
            'focus:outline-none focus:ring-2 focus:ring-brand-600 focus:border-transparent',
            'disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:dark:bg-neutral-900/60 dark:disabled:bg-neutral-900/50',
            'transition-colors duration-150 ease-out',
            error
              ? 'border-danger-400 focus:ring-danger-500 dark:border-danger-500'
              : 'border-neutral-300 dark:border-neutral-700',
            className
          )}
          {...rest}
        />
        {hint && !error && (
          <p id={hintId} className="text-caption text-neutral-500 dark:text-neutral-400">
            {hint}
          </p>
        )}
        {error && (
          <p
            id={errorId}
            role="alert"
            aria-live="polite"
            className="text-caption text-danger-600 dark:text-danger-300"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

export default Textarea;
